// ============================================
// APPLICATION - Query Keys (TanStack Query)
// ============================================

/**
 * Query keys centralizados para TanStack Query
 * Organización jerárquica para invalidaciones precisas
 */

export const queryKeys = {
  // ---------- AUTH ----------
  auth: {
    all: ['auth'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const,
    checkSignupStatus: (userId: string | null, almacenId: string | null) =>
      [...queryKeys.auth.all, 'check-signup-status', userId, almacenId] as const,
  },

  // ---------- PERMISOS ----------
  permisos: {
    all: ['permisos'] as const,
    catalogo: (moduloCodigo?: string) => [...queryKeys.permisos.all, 'catalogo', moduloCodigo] as const,
    misPermisos: () => [...queryKeys.permisos.all, 'mis-permisos'] as const,
    deUsuario: (usuarioId: number) => [...queryKeys.permisos.all, 'usuario', usuarioId] as const,
  },

  // ---------- TOURS (onboarding guiado) ----------
  tours: {
    all: ['tours'] as const,
    vistos: () => [...queryKeys.tours.all, 'vistos'] as const,
  },

  // ---------- DASHBOARD ----------
  dashboard: {
    all: ['dashboard'] as const,
    summary: (filtros?: unknown) =>
      [...queryKeys.dashboard.all, 'summary', filtros] as const,
    ventasPeriodo: (params: unknown) =>
      [...queryKeys.dashboard.all, 'ventas-periodo', params] as const,
  },

  // ---------- PRODUCTOS ----------
  productos: {
    all: ['productos'] as const,
    lists: () => [...queryKeys.productos.all, 'list'] as const,
    list: (filtros?: unknown) =>
      [...queryKeys.productos.lists(), filtros] as const,
    details: () => [...queryKeys.productos.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.productos.details(), id] as const,
    search: (query: string) =>
      [...queryKeys.productos.all, 'search', query] as const,
  },

  // ---------- CLIENTES ----------
  clientes: {
    all: ['clientes'] as const,
    lists: () => [...queryKeys.clientes.all, 'list'] as const,
    list: (params?: unknown) =>
      [...queryKeys.clientes.lists(), params] as const,
    details: () => [...queryKeys.clientes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.clientes.details(), id] as const,
    search: (query: string) =>
      [...queryKeys.clientes.all, 'search', query] as const,
  },

  // ---------- VENTAS ----------
  ventas: {
    all: ['ventas'] as const,
    lists: () => [...queryKeys.ventas.all, 'list'] as const,
    list: (filtros?: unknown) =>
      [...queryKeys.ventas.lists(), filtros] as const,
    details: () => [...queryKeys.ventas.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.ventas.details(), id] as const,
    outbox: () => [...queryKeys.ventas.all, 'outbox'] as const,
  },

  // ---------- CAJA ----------
  caja: {
    all: ['caja'] as const,
    cuadraturas: () => [...queryKeys.caja.all, 'cuadraturas'] as const,
    apertura: (id?: string) => [...queryKeys.caja.all, 'apertura', id] as const,
    cierre: (id?: string) => [...queryKeys.caja.all, 'cierre', id] as const,
    vendedores: () => [...queryKeys.caja.all, 'vendedores'] as const,
    cuadraturasAbiertas: () => [...queryKeys.caja.all, 'cuadraturas-abiertas'] as const,
    cuadraturasConFiltros: (filtros: unknown) => [...queryKeys.caja.all, 'cuadraturas-filtros', filtros] as const,
    // ---- módulo nuevo (turno_caja), coexiste con las keys viejas de arriba ----
    turnoActual: () => [...queryKeys.caja.all, 'turno-actual'] as const,
    movimientosTurno: (turnoId: number, page: number = 0) => [...queryKeys.caja.all, 'movimientos', turnoId, page] as const,
    configuracionNueva: () => [...queryKeys.caja.all, 'configuracion-nueva'] as const,
    turnos: (filtros: unknown) => [...queryKeys.caja.all, 'turnos', filtros] as const,
    turnosAbiertos: (page: number, sort?: string) => [...queryKeys.caja.all, 'turnos-abiertos', page, sort] as const,
    turnoDetalle: (id: number) => [...queryKeys.caja.all, 'turno', id] as const,
    movimientosPendientes: (page: number, sort?: string) => [...queryKeys.caja.all, 'movimientos-pendientes', page, sort] as const,
    movimientosPendientesCount: ['caja', 'movimientos-pendientes-count'] as const,
    movimientosResueltos: (page: number, sort?: string) => [...queryKeys.caja.all, 'movimientos-resueltos', page, sort] as const,
    movimientosDeshacerLog: (page: number, sort?: string) => [...queryKeys.caja.all, 'movimientos-deshacer-log', page, sort] as const,
  },

  // ---------- TUTORIALES ----------
  tutoriales: {
    all: ['tutoriales'] as const,
    modulos: ['tutoriales', 'modulos'] as const,
    modulo: (moduleName: string) => [...queryKeys.tutoriales.all, 'modulo', moduleName] as const,
  },

  // ---------- BOLETAS ----------
  boletas: {
    all: ['boletas'] as const,
    conFiltros: (filtros: unknown) => [...queryKeys.boletas.all, 'filtros', filtros] as const,
    details: () => [...queryKeys.boletas.all, 'detail'] as const,
    detail: (boletaId: number) => [...queryKeys.boletas.details(), boletaId] as const,
  },

  // ---------- SUCURSALES ----------
  sucursales: {
    all: ['sucursales'] as const,
  },

  // ---------- USUARIOS ----------
  usuarios: {
    all: ['usuarios'] as const,
  },

  // ---------- ECOMMERCE ----------
  ecommerce: {
    all: ['ecommerce'] as const,
    pedidos: ['ecommerce', 'pedidos'] as const,
    pendientesCount: ['ecommerce', 'pendientes-count'] as const,
  },

  // ---------- FIADOS ----------
  fiados: {
    all: ['fiados'] as const,
    pendientes: ['fiados', 'pendientes'] as const,
    historico: ['fiados', 'historico'] as const,
  },

  // ---------- SII ----------
  sii: {
    all: ['sii'] as const,
    certificate: () => [...queryKeys.sii.all, 'certificate'] as const,
    folios: () => [...queryKeys.sii.all, 'folios'] as const,
  },

  // ---------- CATEGORIAS ALMACEN ----------
  categoriasAlmacen: {
    all: ['categorias-almacen'] as const,
    list: () => [...queryKeys.categoriasAlmacen.all, 'list'] as const,
  },

  // ---------- SUSCRIPCION ----------
  suscripcion: {
    all: ['suscripcion'] as const,
    prospecto: () => [...queryKeys.suscripcion.all, 'prospecto'] as const,
    customer: (rut: string) => [...queryKeys.suscripcion.all, 'customer', rut] as const,
    subscription: (subscriptionId: string) => [...queryKeys.suscripcion.all, 'subscription', subscriptionId] as const,
    invoice: (invoiceId: number) => [...queryKeys.suscripcion.all, 'invoice', invoiceId] as const,
  },

  // ---------- FEATURE FLAGS ----------
  featureFlags: {
    all: ['feature-flags'] as const,
    check: (feature: string) => [...queryKeys.featureFlags.all, 'check', feature] as const,
  },

  // ---------- GRANEL ----------
  granel: {
    all: ['granel'] as const,
    categorias: () => [...queryKeys.granel.all, 'categorias'] as const,
    tipos: (categoriaId: number | null) => [...queryKeys.granel.all, 'tipos', categoriaId] as const,
    productosEstandar: (tipoId: number | null) => [...queryKeys.granel.all, 'productos-estandar', tipoId] as const,
    productos: () => [...queryKeys.granel.all, 'productos'] as const,
  },
} as const;

// Exportaciones de acceso rápido
export const tutorialesKeys = queryKeys.tutoriales;
export const boletasKeys = queryKeys.boletas;
